import type { ContactImportFileResult, ContactImportTagChoice, ContactQuery } from "./contactModel";
import { contactUrl, creationDated, emptyQuery } from "./contactModel";

// Estado de la lista en esta pestaña (ContactViewState del portal actual), aislado por empresa y persona: la consulta,
// la selección, las columnas, el desplazamiento y el lote de importación. Se guarda en sessionStorage como en el portal
// actual; nunca guarda datos de contactos ni el archivo de una importación.

/** Borrador de la importación en memoria (nunca en sessionStorage), del mismo actor y sesión. */
export type ImportPreparation = {
  owner: string;
  sessionStamp: string | null;
  text: string | null;
  fileName: string | null;
  separator: string;
  key: string;
  detectedSeparator: string;
  fileOptionsOpen: boolean;
  file: ContactImportFileResult | null;
  mapping: Record<string, number>;
  phase: number;
  pendingOnly: boolean;
  bheliExport: boolean;
  visible: boolean;
  batchId: string | null;
  tagChoices: ContactImportTagChoice[];
};

export type ContactViewSnapshot = {
  query: ContactQuery;
  selected: number[];
  columns: string[];
  autoColumns: boolean;
  scroll: number;
  importBatchId: string | null;
  authorityMask: string | null;
  scopeRevision: string | null;
  pendingScopeNotice: boolean;
  sessionStamp: string | null;
};

export type ContactViewState = {
  owner: string;
  query: ContactQuery;
  selected: Set<number>;
  columns: Set<string>;
  autoColumns: boolean;
  scroll: number;
  importBatchId: string | null;
  authorityMask: string | null;
  scopeRevision: string | null;
  sessionStamp: string | null;
  returnToTags: string | null;
  importPreparation: ImportPreparation | null;
  pendingScopeNotice: boolean;
};

const state: ContactViewState = {
  owner: "",
  query: { ...emptyQuery },
  selected: new Set(),
  columns: new Set(),
  autoColumns: true,
  scroll: 0,
  importBatchId: null,
  authorityMask: null,
  scopeRevision: null,
  sessionStamp: null,
  returnToTags: null,
  importPreparation: null,
  pendingScopeNotice: false,
};

/** El estado de la pestaña (uno por aplicación, como el servicio de Blazor). */
export const viewState = (): ContactViewState => state;

export function clearViewState(): void {
  state.query = { ...emptyQuery };
  state.selected.clear();
  state.columns.clear();
  state.autoColumns = true;
  state.scroll = 0;
  state.importBatchId = null;
  state.importPreparation = null;
  state.authorityMask = null;
  state.scopeRevision = null;
  state.sessionStamp = null;
  state.returnToTags = null;
  state.pendingScopeNotice = false;
  state.owner = "";
}

/** Otra empresa o persona vacía el estado. */
export function setViewOwner(tenant: number, user: number): void {
  const owner = `${tenant}:${user}`;
  if (state.owner === owner) return;
  clearViewState();
  state.owner = owner;
}

export const snapshot = (): ContactViewSnapshot => ({
  query: state.query,
  selected: [...state.selected],
  columns: [...state.columns],
  autoColumns: state.autoColumns,
  scroll: state.scroll,
  importBatchId: state.importBatchId,
  authorityMask: state.authorityMask,
  scopeRevision: state.scopeRevision,
  pendingScopeNotice: state.pendingScopeNotice,
  sessionStamp: state.sessionStamp,
});

export function restore(value: ContactViewSnapshot): void {
  state.query = creationDated(value.query);
  state.selected = new Set(value.selected);
  state.columns = new Set(value.columns);
  state.autoColumns = value.autoColumns;
  state.scroll = value.scroll;
  state.importBatchId = value.importBatchId;
  state.authorityMask = value.authorityMask;
  state.scopeRevision = value.scopeRevision;
  state.pendingScopeNotice = value.pendingScopeNotice;
  state.sessionStamp = value.sessionStamp;
}

/** Dirección de la lista o de un contacto con la consulta actual y el regreso a Etiquetas. */
export const viewUrl = (id: number | null = null, edit: string | null = null) =>
  contactUrl(state.query, id, edit, state.returnToTags);

/** Lee la vista guardada de la persona (readState): otra persona en la misma pestaña borra la anterior. */
export function readStored(owner: string): { ownerChanged: boolean; snapshot: ContactViewSnapshot | null } | null {
  try {
    const previous = sessionStorage.getItem("bheli-contact-owner");
    if (previous && previous !== owner) {
      sessionStorage.removeItem("bheli-contact-view:" + previous);
      sessionStorage.removeItem("bheli-contact-view:" + owner);
    }
    sessionStorage.setItem("bheli-contact-owner", owner);
    const saved = sessionStorage.getItem("bheli-contact-view:" + owner);
    let parsed: ContactViewSnapshot | null = null;
    if (saved) parsed = JSON.parse(saved) as ContactViewSnapshot;
    return { ownerChanged: !!previous && previous !== owner, snapshot: parsed };
  } catch {
    return null;
  }
}

/** Guarda la vista (saveState); si el almacenamiento está bloqueado, el estado en memoria sigue sirviendo. */
export function storeView(owner: string): void {
  try {
    sessionStorage.setItem("bheli-contact-view:" + owner, JSON.stringify(snapshot()));
  } catch {
    /* El estado en memoria sigue disponible. */
  }
}
